/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:19:44 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FormField, VALIDATOR_TYPES } from "lib/schemas";
import { useFieldValidatorsAPI } from "form-builder/services";
import { useFormBuilderStore } from "form-builder/stores";
import { COLORSCHEME } from "theme/styles";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { APIListProgressWrapper, Image, IconText, OSActionButton, OSDeleteButton } from "ui-widgets";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { EmptyFieldValidatorsCTA } from "./EmptyFieldValidatorsCTA";
import { FieldValidatorFormModal } from "./FieldValidatorForm";

export const FieldValidators = () => {
  const { fieldValidators, isMakingAPIRequest, isAPIError, useDelete, removeFieldValidator } = useFieldValidators();

  return (
    <Stack spacing={2}>
      <Typography variant="h6" color={COLORSCHEME.primary}>
        Custom Validators
      </Typography>
      <FieldValidatorFormModal
        fieldValidator={null}
        renderOpenButton={({ handleOpen }) => (
          <OSActionButton style={{ marginLeft: "auto" }} onClick={handleOpen} variant="contained">
            Add Custom Validator
          </OSActionButton>
        )}
      />
      <APIListProgressWrapper
        items={fieldValidators}
        showLoader={isMakingAPIRequest}
        loaderType="linear"
        isAPIError={isAPIError}
        noDataMessage={<EmptyFieldValidatorsCTA />}
      >
        {fieldValidators.map((fieldValidator) => (
          <FieldValidatorFormModal
            key={`${fieldValidator.id}`}
            fieldValidator={fieldValidator}
            renderOpenButton={({ handleOpen }) => (
              <Stack direction="row">
                <ListItemButton selected={null} onClick={handleOpen}>
                  <ListItemText
                    primary={
                      <IconText
                        textComponent={fieldValidator.name}
                        icon={<Image src="/img/validation.png" height={20} width={20} />}
                      />
                    }
                    secondary={VALIDATOR_TYPES[fieldValidator.type]}
                  />
                </ListItemButton>
                <DeleteOSItemDialog
                  useDeleteOSItem={useDelete}
                  itemId={fieldValidator?.id}
                  onDelete={() => removeFieldValidator(fieldValidator?.id)}
                  onRestore={() => {}}
                  dialogTitle={`Delete ${fieldValidator?.name}?`}
                >
                  {({ handleOpen }) => (
                    <OSDeleteButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpen();
                      }}
                      color="error"
                    />
                  )}
                </DeleteOSItemDialog>
              </Stack>
            )}
          />
        ))}
      </APIListProgressWrapper>
    </Stack>
  );
};

const useFieldValidators = () => {
  const { useList, useDelete } = useFieldValidatorsAPI();
  const { isMakingAPIRequest, isAPIError, makeRequest } = useList();
  const { watch } = useOSFormContext<FormField>();
  const validatorIds = watch("validators");
  const addFieldValidator = useFormBuilderStore((state) => state.addFieldValidator);
  const removeFieldValidator = useFormBuilderStore((state) => state.removeFieldValidator);
  const fieldValidators = useFormBuilderStore((state) =>
    state.fieldValidators.filter((fieldValidator) => validatorIds.includes(fieldValidator.id))
  );

  React.useEffect(() => {
    validatorIds?.length &&
      makeRequest({
        apiQueryParams: { id: validatorIds },
        setValidAPIResponse: (fieldValidators) => fieldValidators.forEach((validator) => addFieldValidator(validator)),
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { fieldValidators, isMakingAPIRequest, isAPIError, useDelete, removeFieldValidator };
};
